import * as THREE from 'three';
import {Vertex, TexCoord, Normal, Face, Element3D, Thumbnail} from '../segment-parser/element';
import {Bookmark} from './bookmark';

export const NUMBER_OF_FACES = 5000;

export interface FaceElement {
    vertex: Vertex,
    texCoord?: TexCoord,
    normal?: Normal,
}

export interface Segment {
    vertices: Vertex[],
    texCoords: TexCoord[],
    normals: Normal[],
    faces: Face[],
}

interface Color {
    r: number,
    g: number,
    b: number,
    a?: number,
}

interface Mesh extends THREE.Mesh<BufferGeometry, Material> {
    added?: boolean,
}

export class Material extends THREE.ShaderMaterial {
    name: string;
    mapPath?: string;
    numberOfFaces?: number;

    constructor(options?: THREE.ShaderMaterialParameters) {
        super(options);
        this.name = "";
    }
}

export class BufferGeometry extends THREE.BufferGeometry {
    currentFaceIndex: number;
    faceNumber: number;

    constructor(faceNumber: number) {
        super();
        this.currentFaceIndex = 0;
        this.faceNumber = faceNumber;

        let verticesArray = new Float32Array(faceNumber * 3 * 3);
        let texCoordsArray = new Float32Array(faceNumber * 3 * 2);
        let normalsArray = new Float32Array(faceNumber * 3 * 3);

        this.setAttribute('position', new THREE.BufferAttribute(verticesArray, 3));
        this.setAttribute('uv', new THREE.BufferAttribute(texCoordsArray, 2));
        this.setAttribute('normal', new THREE.BufferAttribute(normalsArray, 3));
    }

    addFace(fe1: FaceElement, fe2: FaceElement, fe3: FaceElement): boolean {
        if (this.currentFaceIndex >= this.faceNumber) {
            return false;
        }

        let pos = this.attributes.position;
        let tex = this.attributes.uv;
        let norm = this.attributes.normal;

        let posArray = pos.array as Float32Array;
        let texArray = tex.array as Float32Array;
        let normArray = norm.array as Float32Array;

        posArray[this.currentFaceIndex * 9] = fe1.vertex.x;
        posArray[this.currentFaceIndex * 9 + 1] = fe1.vertex.y;
        posArray[this.currentFaceIndex * 9 + 2] = fe1.vertex.z;
        posArray[this.currentFaceIndex * 9 + 3] = fe2.vertex.x;
        posArray[this.currentFaceIndex * 9 + 4] = fe2.vertex.y;
        posArray[this.currentFaceIndex * 9 + 5] = fe2.vertex.z;
        posArray[this.currentFaceIndex * 9 + 6] = fe3.vertex.x;
        posArray[this.currentFaceIndex * 9 + 7] = fe3.vertex.y;
        posArray[this.currentFaceIndex * 9 + 8] = fe3.vertex.z;
        pos.needsUpdate = true;

        if (fe1.texCoord !== undefined) {
            texArray[this.currentFaceIndex * 6] = fe1.texCoord.x;
            texArray[this.currentFaceIndex * 6 + 1] = fe1.texCoord.y;
            tex.needsUpdate = true;
        }

        if (fe2.texCoord !== undefined) {
            texArray[this.currentFaceIndex * 6 + 2] = fe2.texCoord.x;
            texArray[this.currentFaceIndex * 6 + 3] = fe2.texCoord.y;
            tex.needsUpdate = true;
        }

        if (fe3.texCoord !== undefined) {
            texArray[this.currentFaceIndex * 6 + 4] = fe3.texCoord.x;
            texArray[this.currentFaceIndex * 6 + 5] = fe3.texCoord.y;
            tex.needsUpdate = true;
        }

        if (fe1.normal !== undefined) {
            normArray[this.currentFaceIndex * 9] = fe1.normal.x;
            normArray[this.currentFaceIndex * 9 + 1] = fe1.normal.y;
            normArray[this.currentFaceIndex * 9 + 2] = fe1.normal.z;
            norm.needsUpdate = true;
        }

        if (fe2.normal !== undefined) {
            normArray[this.currentFaceIndex * 9 + 3] = fe2.normal.x;
            normArray[this.currentFaceIndex * 9 + 4] = fe2.normal.y;
            normArray[this.currentFaceIndex * 9 + 5] = fe2.normal.z;
            norm.needsUpdate = true;
        }

        if (fe3.normal !== undefined) {
            normArray[this.currentFaceIndex * 9 + 6] = fe3.normal.x;
            normArray[this.currentFaceIndex * 9 + 7] = fe3.normal.y;
            normArray[this.currentFaceIndex * 9 + 8] = fe3.normal.z;
            norm.needsUpdate = true;
        }

        this.currentFaceIndex++;
        return true;
    }
}

function setMaterialTextureFromColor(material: Material, color: Color) {

    let canvas, fillStyle, transparent = false;
    if (color.a === undefined) {
        fillStyle = "rgb(" +
            color.r + ',' +
            color.g + ',' +
            color.b + ')';
    } else {
        fillStyle = "rgba(" +
            color.r + ',' +
            color.g + ',' +
            color.b + ',' +
            color.a + ")";
        transparent = true;
    }

    canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    let ctx = canvas.getContext('2d');
    if (ctx === null) {
        throw new Error("cannot create 2d context");
    }
    ctx.fillStyle = fillStyle;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // material.uniforms.map = {type: "t", value: new THREE.Texture(canvas)};
    material.uniforms.map = {value: new THREE.Texture(canvas)};

    material.uniforms.map.value.needsUpdate = true;
    material.uniforms.map.value.wrapS = THREE.RepeatWrapping;
    material.uniforms.map.value.wrapT = THREE.RepeatWrapping;
    material.side = THREE.DoubleSide;
    material.transparent = transparent;
}

export class Model extends THREE.Object3D {
    parts: Mesh[];
    currentPart?: Mesh;
    segments: Segment[];
    materials: {[key: string]: Material};
    textures: {[key: string]: THREE.Texture};
    averageColors: {[key: string]: Color};
    bookmarks: THREE.Object3D;
    bookmarkThumbnails: {[key: string]: Thumbnail};

    constructor() {
        super();
        this.parts = [];
        this.segments = [];
        this.materials = {};
        this.textures = {};
        this.averageColors = {};
        this.bookmarks = new THREE.Object3D();
        this.add(this.bookmarks);
        this.bookmarkThumbnails = {};
    }

    currentSegment(): Segment | undefined {
        return this.segments[this.segments.length - 1];
    }

    newPart(material: Material) {
        let part = new THREE.Mesh(new BufferGeometry(material.numberOfFaces || NUMBER_OF_FACES), material);
        part.frustumCulled = false;
        this.currentPart = part;
        this.parts.push(part);
    }

    newSegment() {
        this.segments.push({
            vertices: [],
            texCoords: [],
            normals: [],
            // We don't need no face in segment, we'll put them in the parts
            // EDIT: Ok maybe it can still be useful for some analysis
            faces: []
        });
    }

    addMaterial(material: Material) {
        this.materials[material.name] = material;

        if (material.mapPath !== undefined) {
            let average = this.averageColors[material.mapPath];
            if (average !== undefined) {
                setMaterialTextureFromColor(material, average);
            }
        } else {
            setMaterialTextureFromColor(material, {r: 200, g: 200, b: 200});
        }
    }

    addElements(elements: Element3D[]) {
        let currentSegment = this.currentSegment();

        if (currentSegment === undefined) {
            throw new Error("Cannot add elements to inexistant segment");
        }

        let material;

        for (let element of elements) {
            switch (element.type) {
                case "Vertex":
                    currentSegment.vertices.push(element);
                    break;

                case "TexCoord":
                    currentSegment.texCoords.push(element);
                    break;

                case "Normal":
                    currentSegment.normals.push(element);
                    break;

                case "Face":
                    this.visitFace(element);
                    break;

                case "UseMtl":
                    material = this.materials[element.name];
                    if (material !== undefined) {
                        this.setPartByMaterial(material);
                    }
                    break;

                case "NewMtl":
                    material = new Material({
                        uniforms: {
                            diffuse: {
                                value: new THREE.Color().copy(element.diffuse),
                            },
                            textureSize: {
                                value: new THREE.Vector3().copy(element.textureSize),
                            },
                        },
                        fragmentShader: FRAGMENT_SHADER_SRC,
                        vertexShader: VERTEX_SHADER_SRC,
                    });
                    material.name = element.name;
                    material.mapPath = element.mapPath;
                    material.numberOfFaces = element.numberOfFaces;
                    this.addMaterial(material);
                    break;

                case "Texture":
                    let texture = new THREE.Texture(element.bitmap as unknown as HTMLImageElement);
                    texture.name = element.name;
                    this.addTexture(texture);
                    break;

                case "AverageColor":
                    this.averageColors[element.name] = element;
                    break;

                case "Bookmark":
                    let bookmark = new Bookmark(
                        element.baseUrl,
                        element.position,
                        element.target,
                    );
                    this.bookmarks.add(bookmark);
                    break;

                case "Thumbnail":
                    this.bookmarkThumbnails[element.name] = element;
                    break;
            }
        }
    }

    visitFace(face: Face) {
        let segment = this.currentSegment();

        if (segment === undefined || this.currentPart === undefined) {
            throw new Error("Cannot add face to undefined segment or undefined part");
        }

        let faceElement1 = {
            vertex: segment.vertices[face.a.vertex],
            texCoord: face.a.texCoord === undefined ? undefined : segment.texCoords[face.a.texCoord],
            normal: face.a.normal === undefined ? undefined : segment.normals[face.a.normal],
        };

        let faceElement2 = {
            vertex: segment.vertices[face.b.vertex],
            texCoord: face.b.texCoord === undefined ? undefined : segment.texCoords[face.b.texCoord],
            normal: face.b.normal === undefined ? undefined : segment.normals[face.b.normal],
        };

        let faceElement3 = {
            vertex: segment.vertices[face.c.vertex],
            texCoord: face.c.texCoord === undefined ? undefined : segment.texCoords[face.c.texCoord],
            normal: face.c.normal === undefined ? undefined : segment.normals[face.c.normal],
        };

        segment.faces.push(face);

        if (!this.currentPart.geometry.addFace(faceElement1, faceElement2, faceElement3)) {
            this.newPart(this.currentPart.material);
            this.currentPart.geometry.addFace(faceElement1, faceElement2, faceElement3)
        }

        if (this.currentPart.added !== true) {
            this.add(this.currentPart);
            this.currentPart.added = true;
        }

    }

    setPartByMaterial(material: Material) {
        for (let part of this.parts) {
            if (part.material === material) {
                this.currentPart = part;
                return;
            }
        }

        // If we're here, it means we didn't find any corresponding part, so
        // we'll just create a new one
        this.newPart(material);
    }

    addTexture(texture: THREE.Texture) {

        this.textures[texture.name] = texture;

        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

        let materialTextureName;
        let materialReached = 0;

        for (let materialName in this.materials) {
            let material = this.materials[materialName];

            if (materialTextureName === undefined && material.mapPath !== undefined) {
                materialTextureName = material.mapPath;
            }

            if (texture.name === material.mapPath) {

                material.uniforms.map = {value: texture};

                material.uniforms.map.value.needsUpdate = true;
                material.uniforms.map.value.wrapS = THREE.RepeatWrapping;
                material.uniforms.map.value.wrapT = THREE.RepeatWrapping;

                if (material.uniforms.map !== undefined) {
                    material.uniforms.map.value = texture;
                }
                materialReached++;
            }
        }

        if (materialReached === 0) {
            console.warn("we did not find a material with the corersponding texture");
            console.warn("texture has name " + texture.name);
            console.warn("material texture has name " + materialTextureName);
            console.warn(texture.name);
        }
    }

    getThumbnail(name: string) {
        return this.bookmarkThumbnails[name];
    }
}


const VERTEX_SHADER_SRC = `
    varying vec2 vTexCoords;
    varying vec3 vNormal;

    uniform vec3 textureSize;

    void main() {
        vTexCoords = vec2(uv.x * textureSize.x, uv.y * textureSize.y);
        vNormal = normal;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const FRAGMENT_SHADER_SRC = `
    varying vec2 vTexCoords;
    varying vec3 vNormal;

    uniform sampler2D map;
    uniform vec3 diffuse;

    vec3 ambientLight = vec3(0.7,0.7,0.7);
    vec3 directionnalLight1 = normalize(vec3(10,5,7));
    vec3 directionnalLightFactor1 = vec3(0.7 ,0.7, 0.7);
    vec3 directionnalLight2 = normalize(vec3(-10, 5, 7));
    vec3 directionnalLightFactor2 = vec3(0.5, 0.5, 0.5);

    void main() {

        vec3 lambertComponent1 = dot(directionnalLight1, vNormal) * directionnalLightFactor1;
        lambertComponent1 = max(vec3(0.0, 0.0, 0.0), lambertComponent1);

        vec3 lambertComponent2 = dot(directionnalLight2, vNormal) * directionnalLightFactor2;
        lambertComponent2 = max(vec3(0.0, 0.0, 0.0), lambertComponent2);

        vec4 factor = vec4(ambientLight + lambertComponent1 + lambertComponent2, 1.0);

        // vec4 color = factor * vec4(diffuse, 1.0) * texture2D(map, vTexCoords);
        vec4 color = factor * texture2D(map, vTexCoords);

        if (color.a < 0.01) {
            discard;
        } else {
            color.a = 1.0;
        }

        gl_FragColor = color;

    }
`;
